import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type ProviderEntityType = "provider" | "lan-utan-uc" | "foretagslan";
export type ProviderLiveStatus = "fresh" | "stale" | "needs_review" | "failed";

export interface ProviderLiveTerms {
  id: string;
  entity_type: ProviderEntityType;
  entity_slug: string;
  entity_name: string;
  source_name: string | null;
  source_url: string | null;
  status: ProviderLiveStatus;
  confidence: number;
  loan_amount_label: string | null;
  loan_amount_min: number | null;
  loan_amount_max: number | null;
  nominal_rate_label: string | null;
  nominal_rate_min: number | null;
  nominal_rate_max: number | null;
  effective_rate_label: string | null;
  effective_rate_min: number | null;
  effective_rate_max: number | null;
  term_label: string | null;
  term_min_months: number | null;
  term_max_months: number | null;
  setup_fee_label: string | null;
  invoice_fee_label: string | null;
  monthly_fee_label: string | null;
  payout_time_label: string | null;
  credit_check_label: string | null;
  accepts_payment_remarks: boolean | null;
  payment_remarks_label: string | null;
  lender_count: number | null;
  last_scraped_at: string | null;
  last_changed_at: string | null;
  last_verified_at: string | null;
  stale_at: string | null;
}

type ProviderLiveTermsResult<T> = {
  data: T;
  error: {
    code?: string;
    message?: string;
  } | null;
};

type ProviderLiveTermsQuery = PromiseLike<ProviderLiveTermsResult<ProviderLiveTerms[] | null>> & {
  select: (columns: string) => ProviderLiveTermsQuery;
  eq: (column: string, value: string) => ProviderLiveTermsQuery;
  in: (column: string, values: string[]) => ProviderLiveTermsQuery;
  maybeSingle: () => Promise<ProviderLiveTermsResult<ProviderLiveTerms | null>>;
};

type ProviderLiveTermsQueryOptions = {
  enabled?: boolean;
};

export type ProviderLiveTermsBySlug = Record<string, ProviderLiveTerms | null>;

const db = supabase as unknown as {
  from: (table: string) => ProviderLiveTermsQuery;
};

const liveTermsSelectColumns = [
  "id",
  "entity_type",
  "entity_slug",
  "entity_name",
  "source_name",
  "source_url",
  "status",
  "confidence",
  "loan_amount_label",
  "loan_amount_min",
  "loan_amount_max",
  "nominal_rate_label",
  "nominal_rate_min",
  "nominal_rate_max",
  "effective_rate_label",
  "effective_rate_min",
  "effective_rate_max",
  "term_label",
  "term_min_months",
  "term_max_months",
  "setup_fee_label",
  "invoice_fee_label",
  "monthly_fee_label",
  "payout_time_label",
  "credit_check_label",
  "accepts_payment_remarks",
  "payment_remarks_label",
  "lender_count",
  "last_scraped_at",
  "last_changed_at",
  "last_verified_at",
  "stale_at",
].join(",");

function logLiveTermsFetchError(context: string, error: ProviderLiveTermsResult<unknown>["error"]) {
  if (!error || !import.meta.env.DEV) return;

  const message = error.message || "Okänt fel";
  console.warn(`${context}: ${message}`);
}

export function getLiveTermsStatus(terms: ProviderLiveTerms | null | undefined): ProviderLiveStatus | "missing" {
  if (!terms) return "missing";
  if (terms.stale_at && new Date(terms.stale_at).getTime() < Date.now()) return "stale";
  return terms.status;
}

export function isUsableLiveTerms(terms: ProviderLiveTerms | null | undefined): terms is ProviderLiveTerms {
  return getLiveTermsStatus(terms) === "fresh" && terms.confidence >= 0.55;
}

export function formatLiveTermsDate(value: string | null | undefined): string {
  if (!value) return "Okänt datum";

  return new Intl.DateTimeFormat("sv-SE", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

export function useProviderLiveTerms(
  entityType: ProviderEntityType,
  entitySlug: string,
  options: ProviderLiveTermsQueryOptions = {}
) {
  return useQuery({
    queryKey: ["provider-live-terms", entityType, entitySlug],
    queryFn: async () => {
      const { data, error } = await db
        .from("provider_live_terms")
        .select(liveTermsSelectColumns)
        .eq("entity_type", entityType)
        .eq("entity_slug", entitySlug)
        .maybeSingle();

      if (error) {
        logLiveTermsFetchError("Kunde inte hämta live-villkor", error);
        return null;
      }

      return data;
    },
    enabled: options.enabled ?? Boolean(entitySlug),
    staleTime: 1000 * 60 * 5,
  });
}

export function useProviderLiveTermsMap(entityType: ProviderEntityType, entitySlugs: string[]) {
  const normalizedSlugs = Array.from(new Set(entitySlugs.filter(Boolean))).sort();

  return useQuery({
    queryKey: ["provider-live-terms-map", entityType, normalizedSlugs],
    queryFn: async (): Promise<ProviderLiveTermsBySlug> => {
      if (normalizedSlugs.length === 0) return {};

      const { data, error } = await db
        .from("provider_live_terms")
        .select(liveTermsSelectColumns)
        .eq("entity_type", entityType)
        .in("entity_slug", normalizedSlugs);

      if (error) {
        logLiveTermsFetchError("Kunde inte hämta live-villkor för jämförelsesida", error);
        return {};
      }

      return (data || []).reduce<ProviderLiveTermsBySlug>((acc, terms) => {
        acc[terms.entity_slug] = terms;
        return acc;
      }, {});
    },
    enabled: normalizedSlugs.length > 0,
    staleTime: 1000 * 60 * 5,
  });
}
